/**
 * Справочник марок и моделей: легковые (`categoryId=1`) + ЛКТ
 * (`categoryId=2` — ГАЗель, Sprinter, Ducato…; дубли между категориями
 * схлопнуты при генерации, у ЛКТ-моделей в JSON стоит `c: 2`).
 *
 * Данные — `vehicle-dictionary.json` рядом, ~512 КБ (516 марок / 5377
 * моделей). Лежат в репозитории Flutter-клиента (`stryzh/assets/vehicle_
 * dictionary.json`) и копируются сюда одним Python-скриптом
 * `stryzh/scripts/build_vehicle_dictionary.py` — синк побайтный.
 *
 * Загружаются динамическим импортом, чтобы Vite вынес ~512 КБ в отдельный
 * chunk, который тянется только на странице создания/редактирования
 * заказа, а не в основном бандле. Парсинг и индексация — один раз
 * за жизнь приложения.
 *
 * Поиск — по предвычисленному полю `s`: в нём через пробел склеены
 * нормализованные формы (lowercase + ё→е) имени и алиасов; для брендов
 * с дефисом — варианты со слитной и раздельной формой. Простое
 * `s.includes(normalizeQuery(input))` находит и EN, и RU варианты.
 */

export interface VehicleBrand {
  id: number
  name: string
  /** Внутреннее поле поиска. Не показывать пользователю. */
  s: string
}

export interface VehicleModel {
  id: number
  /** brandId — числовая ссылка на марку в этом же справочнике. */
  b: number
  name: string
  s: string
  /** Категория: 2 — ЛКТ. Отсутствует у легковых (categoryId=1). */
  c?: number
  /** yearFrom — год начала выпуска (если известен). */
  yf?: number
  /** yearTo — год окончания выпуска (если известен). */
  yt?: number
}

interface RawDict {
  version: number
  /** Категории источника, напр. [1, 2]. До июня 2026 было `category: 1`. */
  categories: number[]
  brands: VehicleBrand[]
  models: VehicleModel[]
}

interface CachedDict {
  brands: VehicleBrand[]
  modelsByBrand: Map<number, VehicleModel[]>
}

let cache: CachedDict | null = null
let loading: Promise<CachedDict> | null = null

/** Дефолтный loader — динамический импорт реального JSON из бандла Vite. */
const defaultLoader: () => Promise<RawDict> = () =>
  import('./vehicle-dictionary.json').then(
    (mod) =>
      (mod as unknown as { default: RawDict }).default ??
      (mod as unknown as RawDict),
  )

let rawLoader: () => Promise<RawDict> = defaultLoader

/**
 * Нормализация строки запроса. ОБЯЗАНА совпадать 1:1 с тем, что делает
 * Python-скрипт (`normalize` в `build_vehicle_dictionary.py`) — иначе
 * `searchText.includes(query)` не сработает.
 */
export function normalizeQuery(q: string): string {
  return q.toLowerCase().replace(/ё/g, 'е').trim()
}

async function load(): Promise<CachedDict> {
  if (cache) return cache
  if (loading) return loading
  loading = rawLoader().then((raw) => {
    const modelsByBrand = new Map<number, VehicleModel[]>()
    for (const m of raw.models) {
      const arr = modelsByBrand.get(m.b)
      if (arr) arr.push(m)
      else modelsByBrand.set(m.b, [m])
    }
    cache = { brands: raw.brands, modelsByBrand }
    return cache
  })
  return loading
}

/** Все марки в порядке, заданном JSON (уже отсортированы при генерации). */
export async function listBrands(): Promise<VehicleBrand[]> {
  return (await load()).brands
}

/** Все модели марки в порядке, заданном JSON. */
export async function modelsOfBrand(brandId: number): Promise<VehicleModel[]> {
  return (await load()).modelsByBrand.get(brandId) ?? []
}

/** Поиск марок. Пустой запрос → все марки. */
export async function searchBrands(query: string): Promise<VehicleBrand[]> {
  const { brands } = await load()
  const q = normalizeQuery(query)
  if (!q) return brands
  return brands.filter((b) => b.s.includes(q))
}

/** Поиск моделей в пределах одной марки. Пустой запрос → все модели. */
export async function searchModels(
  brandId: number,
  query: string,
): Promise<VehicleModel[]> {
  const all = await modelsOfBrand(brandId)
  const q = normalizeQuery(query)
  if (!q) return all
  return all.filter((m) => m.s.includes(q))
}

/** Найти марку по точному имени. Полезно для резолва имени из старых данных
 *  (заказ в БД хранит `brand` как текст — id неизвестен до резолва). */
export async function findBrandByName(
  name: string,
): Promise<VehicleBrand | null> {
  const { brands } = await load()
  return brands.find((b) => b.name === name) ?? null
}

/**
 * Прогревочный вызов — можно дёрнуть на маршруте, где скоро понадобится
 * пикер, чтобы chunk пришёл заранее.
 */
export function warmUpVehicleDictionary(): void {
  void load()
}

/** Только для тестов: подменить источник данных. */
export function __setVehicleDictionaryLoader(
  loader: () => Promise<RawDict>,
): void {
  rawLoader = loader
  cache = null
  loading = null
}

/** Только для тестов: сбросить кеш (с дефолтным loader). */
export function __resetVehicleDictionary(): void {
  rawLoader = defaultLoader
  cache = null
  loading = null
}
